/**
 * FacialMap — mapa facial persistente del paciente (no por episodio).
 *
 * Una foto frontal de cara (hombre / mujer, según el sexo del paciente) sobre
 * la que el doctor marca puntos (del color de su producto) haciendo clic, con una nota por
 * punto y anotaciones generales. Sirve para dejar registrado dónde se puso
 * relleno, grasa o cirugía.
 *
 * Se guarda en el propio paciente (`patient.facial_map`) — el modelo de
 * paciente acepta campos extra, así que persiste con un PUT normal y queda
 * fijo en la historia clínica, visible siempre. Los marcadores se guardan en %
 * (0..100) para ser independientes del tamaño de la imagen en pantalla.
 *
 * QUÉ SE MARCA lo dice el catálogo de la clínica (V2, 28/9/2026): la lista
 * «Productos del mapa facial» del Catálogo de cirugías (`lib/listasClinica`),
 * que edita un administrador. Aquí no hay ninguno escrito. Sin productos, los
 * puntos se marcan sin producto y en gris, y Configuración lo avisa.
 *
 * EL ENCUADRE de la foto (28/9/2026, dirección): con «Ajustar encuadre» se
 * acerca y se mueve la foto del paciente, y queda guardado POR FOTO en
 * `facial_map.encuadres`. Se acerca y se mueve una capa con la foto Y los
 * puntos; los puntos siguen en % de la foto entera. Las cuentas, en
 * `lib/encuadreMapaFacial`.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Save, Trash2, MapPin, ChevronDown, ChevronUp, Eraser,
  Crop, ZoomIn, ZoomOut, RotateCcw, Check,
} from 'lucide-react';
import { api, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { PRODUCTOS_MAPA_FACIAL, useListaClinica } from '../../lib/listasClinica';
import {
  SIN_ENCUADRE, ZOOM_MAXIMO, conEncuadre, encuadreHacia, encuadreSeguro,
  encuadresGuardados, encuadresParaGuardar, mismoEncuadre, puntoDeLaFoto, transformDe,
} from '../../lib/encuadreMapaFacial';
import { SectionCard } from '../Common';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import faceMale from '../../assets/face_male.jpg';
import faceFemale from '../../assets/face_female.jpg';

// Qué se ha puesto en cada punto. El color es solo la forma de verlo: lo que
// se guarda es el PRODUCTO (2026-08-19), por su `clave`. Los productos son los
// del catálogo (`{clave, nombre, corto, color, activo, claves_antiguas}`); el
// primero activo es el que sale elegido al abrir el mapa.

// Los puntos más antiguos se guardaron con un color ('rojo'/'amarillo') en vez
// de un producto. A qué producto cuentan lo dice el catálogo
// (`claves_antiguas`), y se pintan del color de ESE producto. No se reescribe
// la base.
const COLOR_DE_LOS_PUNTOS_ANTIGUOS = { rojo: '#e11d48', amarillo: '#f59e0b' };
// Un punto sin producto (o de uno que ya no está en el catálogo).
const COLOR_SIN_PRODUCTO = '#64748b';

/** El producto de un punto, entendiendo también los puntos antiguos. */
export function productoDe(marker, productos) {
  const c = marker?.producto || marker?.color || '';
  if (!c) return null;
  return productos.find((p) => p.clave === c)
    || productos.find((p) => (p.claves_antiguas || []).includes(c)) || null;
}

/**
 * El color con el que se pinta: el del producto al que CUENTA el punto
 * (`productoDe`), sea nuevo o antiguo (28/9/2026: un punto antiguo
 * «amarillo» que cuenta como otro producto salía amarillo aunque su producto
 * fuera de otro color, y si dirección ponía el lipofilling en amarillo se
 * confundían). Así el color cuadra con la cuenta del título y con los
 * botones de «Qué pongo». Solo un punto antiguo que no cuenta a ningún
 * producto sale de su color de entonces; sin producto, gris.
 */
export function colorDe(marker, productos) {
  const suyo = productoDe(marker, productos);
  if (suyo?.color) return suyo.color;
  const c = marker?.producto || marker?.color || '';
  return COLOR_DE_LOS_PUNTOS_ANTIGUOS[c] || COLOR_SIN_PRODUCTO;
}
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// Lo que acerca o aleja cada pulsación de + / − al ajustar el encuadre.
const PASO_ZOOM = 1.25;

/** Normaliza el sexo a 'M' (hombre) / 'F' (mujer). Admite los valores de la
 *  LOPD ("M"/"F"), de Clinic Cloud ("Masculino"/"Femenino") y variantes. */
function normSexo(v) {
  const s = String(v || '').trim().toLowerCase();
  if (!s) return '';
  if (s.startsWith('f') || s.startsWith('muj') || s.startsWith('wom')) return 'F';
  if (s.startsWith('m') || s.startsWith('h') || s.startsWith('v')) return 'M';
  return '';
}

export default function FacialMap({ patient, onPatientUpdated, apilado = false }) {
  const { user } = useAuth();
  const fm = patient.facial_map || {};
  const [markers, setMarkers] = useState(fm.markers || []);
  const [notes, setNotes] = useState(fm.notes || '');
  const [sexo, setSexo] = useState(fm.sexo || normSexo(patient.sexo) || 'F');
  const [productos] = useListaClinica(PRODUCTOS_MAPA_FACIAL);
  const ofrecidos = useMemo(() => productos.filter((p) => p.activo !== false), [productos]);
  const [elegido, setProducto] = useState(null);
  // Hasta que alguien elige, el primero activo del catálogo.
  const producto = (elegido && ofrecidos.some((p) => p.clave === elegido))
    ? elegido : (ofrecidos[0]?.clave || '');
  const [open, setOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Versión de cambios: si el usuario marca un punto MIENTRAS un guardado
  // está en vuelo, el éxito de ese guardado no debe limpiar dirty
  // (revisión 2026-08-27: el punto se perdía en el modo apilado).
  const versionRef = useRef(0);
  // Foto del paciente como fondo del mapa (2026-07-17): se puede marcar sobre la
  // foto REAL — la del "antes" o la "comparativa" (collage) — o sobre la genérica.
  const [fotos, setFotos] = useState([]);
  const [fondo, setFondo] = useState(fm.fondo || (fm.usar_foto === false ? 'generica' : 'antes'));
  // Qué foto de «antes» ve el médico en el mapa (2026-08-27, dirección): puede
  // elegir entre las que subió el paciente (frontal/oblicua), pero la
  // preferencia por defecto es SIEMPRE la frontal.
  const [fotoAntesId, setFotoAntesId] = useState(fm.foto_antes_id || null);
  // El encuadre de cada foto de fondo (28/9/2026): `{<id de la foto>: {zoom, x, y}}`.
  const [encuadres, setEncuadres] = useState(() => encuadresGuardados(fm.encuadres));
  // «Ajustar encuadre»: mientras dura, arrastrar mueve la foto y un clic no marca.
  const [ajustando, setAjustando] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const boxRef = useRef(null);
  // Los dedos / el ratón sobre la foto y el gesto en curso (arrastre o pellizco).
  const punteros = useRef(new Map());
  const gesto = useRef(null);

  // Reengancha cuando cambia el paciente o llega una actualización.
  useEffect(() => {
    const f = patient.facial_map || {};
    setMarkers(f.markers || []);
    setNotes(f.notes || '');
    setSexo(f.sexo || normSexo(patient.sexo) || 'F');
    setFondo(f.fondo || (f.usar_foto === false ? 'generica' : 'antes'));
    setFotoAntesId(f.foto_antes_id || null);
    setEncuadres(encuadresGuardados(f.encuadres));
    setDirty(false);
  }, [patient.id, patient.facial_map, patient.sexo]);

  // Otro paciente: fuera del modo de ajustar.
  useEffect(() => { setAjustando(false); }, [patient.id]);

  // Carga las fotos del paciente (para usar "antes" o "comparativa" de fondo).
  useEffect(() => {
    let cancelled = false;
    api.get(`/api/patients/${patient.id}/fotos`)
      .then((r) => { if (!cancelled) setFotos(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setFotos([]); });
    return () => { cancelled = true; };
  }, [patient.id]);

  // La lista viene ordenada por fecha desc: la primera de cada tipo es la más reciente.
  // La foto de fondo por defecto es la FRONTAL (2026-08-25, dirección): el
  // enlace sube frontal y oblicua, y como la oblicua entra la última quedaba
  // ella delante. Se busca primero una 'antes' frontal (por su pie de foto);
  // si no la hay, la 'antes' más reciente, como siempre.
  const deAntes = useMemo(() => fotos.filter((f) => (f.tipo || '') === 'antes'), [fotos]);
  const antesFoto = useMemo(() => {
    const elegida = fotoAntesId ? deAntes.find((f) => f.id === fotoAntesId) : null;
    return elegida || deAntes.find((f) => /frontal/i.test(f.caption || '')) || deAntes[0] || null;
  }, [deAntes, fotoAntesId]);
  const etiquetaFoto = (f, i) => {
    if (/frontal/i.test(f.caption || '')) return 'Frontal';
    if (/oblicua|45/i.test(f.caption || '')) return 'Oblicua';
    return `Foto ${i + 1}`;
  };
  const compFoto = useMemo(() => fotos.find((f) => (f.tipo || '') === 'comparativa') || null, [fotos]);
  const fotoFondo = fondo === 'comparativa' ? compFoto : fondo === 'antes' ? antesFoto : null;
  const usandoFoto = !!fotoFondo;
  // `?tam=mapa` (revisión 24/9/2026): la foto COMO SE GUARDÓ, que es sobre la
  // que se pusieron los puntos, y en tamaño medio (1200 px, no la de hasta
  // 10 MB). Sin él, `/img` sirve la foto ENTERA, y en las que se recortaron
  // a la cara del 8/9 al 24/9 el recuadro pasaba de 4:5 a 3:4, la cara salía
  // más pequeña y los puntos de infiltración caían en otro sitio.
  const fotoUrl = fotoFondo ? `${BACKEND_URL}/api/patients/${patient.id}/fotos/${fotoFondo.id}/img?tam=mapa` : null;
  // El encuadre de la foto que se ve (la cara genérica, siempre entera).
  const idFoto = fotoFondo ? fotoFondo.id : null;
  const encuadre = idFoto ? encuadreSeguro(encuadres[idFoto]) : SIN_ENCUADRE;
  // El último puesto, para los gestos, que llegan más rápido que los renders.
  const encuadreRef = useRef(encuadre);
  encuadreRef.current = encuadre;
  const enAjuste = ajustando && !!idFoto;
  const acercada = encuadre.zoom > 1;

  const meta = fm.updated_at
    ? `Última edición: ${formatDateTime(fm.updated_at)}${fm.updated_by_name ? ` · ${fm.updated_by_name}` : ''}`
    : null;

  const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `m${Date.now()}${Math.random().toString(36).slice(2, 7)}`);

  const faceSrc = sexo === 'M' ? faceMale : faceFemale;

  // Un punto de la pantalla, en % del recuadro (que no se mueve: lo que se
  // acerca es la capa de dentro). Sin caja no hay foto pintada.
  const enElRecuadro = (clientX, clientY) => {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect || !(rect.width > 0) || !(rect.height > 0)) return null;
    return { x: ((clientX - rect.left) / rect.width) * 100, y: ((clientY - rect.top) / rect.height) * 100 };
  };

  const addMarker = (e) => {
    // Ajustando el encuadre, tocar la foto es para moverla, no para marcar.
    if (enAjuste) return;
    const b = enElRecuadro(e.clientX, e.clientY);
    if (!b) return;
    // El punto se guarda en % de la FOTO: con la foto acercada, lo que hay
    // bajo el clic (la inversa del encuadre).
    const { x, y } = puntoDeLaFoto(encuadre, b.x, b.y);
    setMarkers((prev) => [...prev, { id: uid(), x, y, producto, color: producto, note: '' }]);
    versionRef.current += 1;
    setDirty(true);
  };

  // ------------------------------------------------ el encuadre de la foto
  const ponerEncuadre = (nuevo) => {
    if (!idFoto) return;
    const limpio = encuadreSeguro(nuevo);
    if (mismoEncuadre(limpio, encuadreRef.current)) return;
    encuadreRef.current = limpio;
    setEncuadres((prev) => conEncuadre(prev, idFoto, limpio));
    versionRef.current += 1;
    setDirty(true);
  };
  const acercar = (factor) => ponerEncuadre(encuadreHacia(encuadreRef.current, encuadreRef.current.zoom * factor));

  const soltarGestos = () => {
    punteros.current.clear();
    gesto.current = null;
    setArrastrando(false);
  };

  // «Listo»: se sale del modo y se guarda con el guardado de siempre (junto
  // al evolutivo lo hace el guardado automático, que espera a que acabe).
  const terminarAjuste = () => {
    setAjustando(false);
    soltarGestos();
    if (!apilado && dirty && !saving) save();
  };

  const gestoDesde = () => {
    const [a, b] = [...punteros.current.values()];
    if (!b) {
      const p = enElRecuadro(a.x, a.y);
      return p && { tipo: 'arrastre', p0: p, v0: encuadreRef.current };
    }
    const medio = enElRecuadro((a.x + b.x) / 2, (a.y + b.y) / 2);
    return medio && {
      tipo: 'pellizco', medio0: medio, d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, v0: encuadreRef.current,
    };
  };

  const alBajar = (e) => {
    if (!enAjuste) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (punteros.current.size >= 2) return;   // un tercer dedo no cuenta
    try { boxRef.current?.setPointerCapture?.(e.pointerId); } catch { /* sin captura */ }
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    gesto.current = gestoDesde();
    setArrastrando(true);
  };

  const alMover = (e) => {
    if (!enAjuste || !punteros.current.has(e.pointerId)) return;
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesto.current;
    if (!g) return;
    if (g.tipo === 'pellizco' && punteros.current.size >= 2) {
      // Lo que se separan los dedos acerca; el punto de la cara que estaba
      // entre los dedos sigue entre los dedos.
      const [a, b] = [...punteros.current.values()];
      const medio = enElRecuadro((a.x + b.x) / 2, (a.y + b.y) / 2);
      if (!medio) return;
      const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      ponerEncuadre(encuadreHacia(g.v0, g.v0.zoom * (d / g.d0), g.medio0, medio));
      return;
    }
    if (g.tipo === 'arrastre') {
      const p = enElRecuadro(e.clientX, e.clientY);
      if (p) ponerEncuadre(encuadreHacia(g.v0, g.v0.zoom, g.p0, p));
    }
  };

  const alSubir = (e) => {
    if (!punteros.current.has(e.pointerId)) return;
    punteros.current.delete(e.pointerId);
    try { boxRef.current?.releasePointerCapture?.(e.pointerId); } catch { /* ya suelto */ }
    if (punteros.current.size > 0) {
      // Queda un dedo del pellizco: sigue arrastrando desde donde está.
      gesto.current = gestoDesde();
      return;
    }
    soltarGestos();
  };

  // La rueda acerca donde está el puntero. Con un oyente de verdad (no
  // pasivo) para que no desplace la página por debajo; solo ajustando: fuera
  // de ese modo la rueda mueve la página como siempre.
  const ruedaRef = useRef(null);
  ruedaRef.current = (e) => {
    e.preventDefault();
    const unidad = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
    const delta = (e.deltaY || 0) * unidad;
    const p = enElRecuadro(e.clientX, e.clientY);
    if (!delta || !p) return;
    const actual = encuadreRef.current;
    // El pellizco del trackpad llega como rueda + Ctrl.
    ponerEncuadre(encuadreHacia(actual, actual.zoom * Math.exp(-delta * (e.ctrlKey ? 0.01 : 0.002)), p));
  };
  useEffect(() => {
    const el = boxRef.current;
    if (!el || !enAjuste) return undefined;
    const alRodar = (e) => ruedaRef.current?.(e);
    el.addEventListener('wheel', alRodar, { passive: false });
    return () => el.removeEventListener('wheel', alRodar);
  }, [enAjuste, open]);

  const setNote = (id, note) => {
    setMarkers((prev) => prev.map((m) => (m.id === id ? { ...m, note } : m)));
    setDirty(true);
  };
  const removeMarker = (id) => {
    setMarkers((prev) => prev.filter((m) => m.id !== id));
    setDirty(true);
  };
  const clearAll = () => {
    if (!markers.length) return;
    if (!window.confirm('¿Borrar todos los puntos del mapa facial?')) return;
    setMarkers([]);
    setDirty(true);
  };

  const changeSexo = (s) => {
    if (s === sexo) return;
    setSexo(s);
    setDirty(true);
  };

  // Junto al evolutivo no hay botón Guardar (nada bajo la cara): cada
  // cambio se guarda solo, con un respiro para agrupar clics seguidos.
  // Mientras se ajusta el encuadre no: se guarda al pulsar «Listo».
  useEffect(() => {
    if (!apilado || !dirty || saving || enAjuste) return undefined;
    const t = setTimeout(() => { save(); }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apilado, dirty, markers, notes, fondo, sexo, fotoAntesId, encuadres, enAjuste]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        facial_map: {
          markers,
          notes,
          sexo,
          fondo,
          usar_foto: fondo !== 'generica',
          foto_id: fotoFondo ? fotoFondo.id : null,
          foto_antes_id: (fotoAntesId && deAntes.some((f) => f.id === fotoAntesId)) ? fotoAntesId : null,
          // El encuadre, uno por foto; los puntos siguen en % de la foto entera.
          encuadres: encuadresParaGuardar(encuadres),
          updated_at: new Date().toISOString(),
          updated_by_name: user?.name || '',
        },
      };
      const versionEnviada = versionRef.current;
      await api.put(`/api/patients/${patient.id}`, payload);
      toast.success('Mapa facial guardado');
      if (versionRef.current === versionEnviada) setDirty(false);
      onPatientUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el mapa facial');
    } finally {
      setSaving(false);
    }
  };

  // Cuántos puntos hay de cada producto, para el título.
  const resumen = useMemo(() => productos
    .map((pr) => ({ ...pr, n: markers.filter((m) => productoDe(m, productos)?.clave === pr.clave).length }))
    .filter((x) => x.n > 0), [markers, productos]);

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#2E6E5E]" /> Mapa facial
          {markers.length > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
              {resumen.map((x) => (
                <span key={x.clave} className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: x.color }} />
                  {x.n} {x.corto}
                </span>
              ))}
            </span>
          )}
        </span>
      }
      action={
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            // Cerrar a medias de ajustar el encuadre es como pulsar «Listo».
            if (open && ajustando) terminarAjuste();
            setOpen((o) => !o);
          }}
          data-testid="facial-map-toggle"
        >
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {open ? 'Ocultar' : 'Abrir'}
        </Button>
      }
    >
      {!open ? (
        <p className="text-sm text-muted-foreground">
          Marca en la cara dónde se puso relleno, grasa o cirugía (queda fijo en la historia). Pulsa «Abrir» para editarlo.
        </p>
      ) : (
        <div className={apilado ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 lg:grid-cols-2 gap-4'}>
          {/* `apilado`: junto al evolutivo la columna es estrecha; partirla en
              dos dejaba la cara diminuta y los puntos salían gordos. La cara a
              todo el ancho y la lista debajo (2026-08-19, dirección: «que la
              cara ocupe más espacio para marcar más preciso»). */}
          {/* Cara + marcadores */}
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-xs text-muted-foreground">Qué pongo:</span>
              {ofrecidos.map((pr) => (
                <button
                  key={pr.clave}
                  type="button"
                  onClick={() => setProducto(pr.clave)}
                  data-testid={`facial-map-producto-${pr.clave}`}
                  title={`Marcar puntos de ${pr.nombre}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                    producto === pr.clave ? 'border-[#13231E] bg-[rgba(0,0,0,0.04)] ring-1 ring-[#13231E]' : 'border-[#D9E3DD]'
                  }`}
                >
                  <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: pr.color }} />
                  {pr.nombre}
                </button>
              ))}
              {ofrecidos.length === 0 && (
                <span className="text-xs text-muted-foreground" data-testid="facial-map-sin-productos">
                  Sin productos en el catálogo: los puntos se marcan sin producto.
                </span>
              )}
              {/* Fondo del mapa: foto "antes", "comparativa" (collage) o cara genérica */}
              <div className="ml-auto inline-flex items-center gap-1 flex-wrap justify-end">
                {(antesFoto || compFoto) && (
                  <>
                    <span className="text-xs text-muted-foreground mr-0.5">Fondo:</span>
                    {[
                      ...(antesFoto ? [['antes', 'Foto antes']] : []),
                      ...(compFoto ? [['comparativa', 'Comparativa']] : []),
                      ['generica', 'Cara genérica'],
                    ].map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => { setFondo(val); setDirty(true); }}
                        data-testid={`facial-map-fondo-${val}`}
                        className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                          (usandoFoto ? fondo : 'generica') === val
                            ? 'border-[#13231E] bg-[rgba(0,0,0,0.04)]'
                            : 'border-[#D9E3DD]'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </>
                )}
                {/* Cara (hombre/mujer) solo cuando se usa la cara genérica */}
                {!usandoFoto && (
                  <>
                    <span className="text-xs text-muted-foreground mr-0.5 ml-1">Cara:</span>
                    {[['F', 'Mujer'], ['M', 'Hombre']].map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => changeSexo(val)}
                        data-testid={`facial-map-sexo-${val}`}
                        className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                          sexo === val ? 'border-[#13231E] bg-[rgba(0,0,0,0.04)]' : 'border-[#D9E3DD]'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
            {usandoFoto && fondo === 'antes' && deAntes.length > 1 && (
              <div className="flex items-center gap-1 justify-end flex-wrap mb-1.5">
                <span className="text-xs text-muted-foreground mr-0.5">Foto:</span>
                {deAntes.map((f, i) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => { setFotoAntesId(f.id); setDirty(true); }}
                    data-testid={`facial-map-foto-${i}`}
                    title={f.caption || ''}
                    className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                      antesFoto?.id === f.id
                        ? 'border-[#13231E] bg-[rgba(0,0,0,0.04)]'
                        : 'border-[#D9E3DD]'
                    }`}
                  >
                    {etiquetaFoto(f, i)}
                  </button>
                ))}
              </div>
            )}
            <div className="rounded-lg border border-[#D9E3DD] bg-white overflow-hidden">
              {/* El recuadro no se mueve y recorta; dentro, la CAPA con la
                  foto y los puntos es lo que se acerca y se mueve (el
                  encuadre). Los puntos van en % de la capa = % de la foto. */}
              <div
                ref={boxRef}
                onClick={addMarker}
                onPointerDown={alBajar}
                onPointerMove={alMover}
                onPointerUp={alSubir}
                onPointerCancel={alSubir}
                className={`relative w-full max-w-[340px] mx-auto select-none ${acercada ? 'overflow-hidden' : ''} ${
                  enAjuste ? (arrastrando ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-crosshair'
                }`}
                style={enAjuste ? { touchAction: 'none' } : undefined}
                data-testid="facial-map-canvas"
              >
                <div
                  className="relative"
                  style={acercada ? { transform: transformDe(encuadre), transformOrigin: '0 0' } : undefined}
                  data-zoom={encuadre.zoom}
                  data-testid="facial-map-capa"
                >
                  <img
                    src={usandoFoto ? fotoUrl : faceSrc}
                    alt={usandoFoto
                      ? (fondo === 'comparativa' ? 'Comparativa del paciente' : 'Foto del paciente (antes)')
                      : (sexo === 'M' ? 'Cara de hombre' : 'Cara de mujer')}
                    className="w-full block pointer-events-none select-none"
                    draggable={false}
                  />
                  {markers.map((m, i) => (
                    <span
                      key={m.id}
                      className="absolute w-5 h-5 rounded-full border-2 border-white inline-flex items-center justify-center text-[10px] font-bold text-white shadow pointer-events-none"
                      style={{
                        left: `${m.x}%`,
                        top: `${m.y}%`,
                        backgroundColor: colorDe(m, productos),
                        // Centrado en su sitio y del mismo tamaño con cualquier zoom.
                        transform: `translate(-50%, -50%)${acercada ? ` scale(${1 / encuadre.zoom})` : ''}`,
                      }}
                    >
                      {i + 1}
                    </span>
                  ))}
                </div>
                {/* Ajustando, un marco encima de la foto (por dentro): se ve
                    que ahora tocar la foto la mueve. */}
                {enAjuste && (
                  <div className="pointer-events-none absolute inset-0 ring-2 ring-inset ring-[#2E6E5E]" aria-hidden="true" />
                )}
              </div>
            </div>
            {enAjuste ? (
              <div className="mt-1.5 space-y-1" data-testid="facial-map-encuadre-barra">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Button type="button" size="sm" variant="outline" className="h-8 w-8 p-0"
                    onClick={() => acercar(1 / PASO_ZOOM)} disabled={!acercada}
                    aria-label="Alejar la foto" title="Alejar" data-testid="facial-map-encuadre-menos">
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <input
                    type="range"
                    min={1}
                    max={ZOOM_MAXIMO}
                    step={0.05}
                    value={encuadre.zoom}
                    onChange={(e) => ponerEncuadre(encuadreHacia(encuadreRef.current, Number(e.target.value)))}
                    aria-label="Zoom de la foto"
                    className="w-28 accent-[#2E6E5E]"
                    data-testid="facial-map-encuadre-zoom"
                  />
                  <Button type="button" size="sm" variant="outline" className="h-8 w-8 p-0"
                    onClick={() => acercar(PASO_ZOOM)} disabled={encuadre.zoom >= ZOOM_MAXIMO}
                    aria-label="Acercar la foto" title="Acercar" data-testid="facial-map-encuadre-mas">
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <span className="text-xs tabular-nums text-muted-foreground min-w-[3rem] text-center" aria-live="polite">
                    {Math.round(encuadre.zoom * 100)} %
                  </span>
                  <Button type="button" size="sm" variant="ghost" className="h-8 px-2 text-xs"
                    onClick={() => ponerEncuadre(SIN_ENCUADRE)} disabled={!acercada}
                    title="Volver a la foto entera" data-testid="facial-map-encuadre-original">
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Encuadre original
                  </Button>
                  <Button type="button" size="sm" onClick={terminarAjuste}
                    className="h-8 ml-auto bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="facial-map-encuadre-listo">
                    <Check className="w-4 h-4 mr-1" /> Listo
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Arrastra la foto para moverla; acerca con + / −, la rueda o pellizcando. Los puntos van pegados a la cara.
                </p>
              </div>
            ) : (
              <div className="mt-1.5 flex flex-wrap items-center gap-3">
                {!apilado && <span className="text-[11px] text-muted-foreground">
                  {usandoFoto ? 'Haz clic en la foto del paciente para marcar' : 'Haz clic en la cara para marcar'}
                </span>}
                {usandoFoto && (
                  <button
                    type="button"
                    onClick={() => setAjustando(true)}
                    className="inline-flex items-center gap-1 text-xs text-[#2E6E5E] hover:text-[#1E4A3F]"
                    title="Acercar y mover la foto; queda guardado para esta foto"
                    data-testid="facial-map-encuadre"
                  >
                    <Crop className="w-3.5 h-3.5" /> Ajustar encuadre
                    {acercada && <span className="text-muted-foreground tabular-nums">({Math.round(encuadre.zoom * 100)} %)</span>}
                  </button>
                )}
                {markers.length > 0 && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-800"
                  >
                    <Eraser className="w-3.5 h-3.5" /> Borrar todos los puntos
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Lista de puntos + anotaciones — junto al evolutivo NO se pinta
              nada bajo la cara (2026-08-20, dirección): el mapa guarda solo y
              las anotaciones se editan en la vista completa de abajo. */}
          {!apilado && <div className="space-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
                Puntos marcados
              </div>
              {markers.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aún no hay puntos. Haz clic en la cara.</p>
              ) : (
                <ol className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {markers.map((m, i) => (
                    <li key={m.id} className="flex items-center gap-2">
                      <span
                        className="w-5 h-5 shrink-0 rounded-full inline-flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ backgroundColor: colorDe(m, productos) }}
                      >
                        {i + 1}
                      </span>
                      <input
                        value={m.note}
                        onChange={(e) => setNote(m.id, e.target.value)}
                        placeholder="Qué se puso aquí…"
                        className="flex-1 min-w-0 h-8 rounded-md border border-[#dbe3ee] px-2 text-xs"
                        data-testid={`facial-map-note-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeMarker(m.id)}
                        className="shrink-0 text-muted-foreground hover:text-red-700"
                        title="Quitar punto"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
                Anotaciones
              </div>
              <Textarea
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
                rows={3}
                placeholder="Notas generales del mapa facial…"
                className="text-xs"
                data-testid="facial-map-notes"
              />
            </div>

            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] text-muted-foreground">{meta}</span>
              <Button
                size="sm"
                onClick={save}
                disabled={saving || !dirty}
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                data-testid="facial-map-save"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Guardar
              </Button>
            </div>
          </div>}
        </div>
      )}
    </SectionCard>
  );
}
